import { useEffect, useRef, useState } from 'react';
import { isMessageEvent, MessageDirection } from "../state/conversation/types";
import { Mode } from "../state/freeTrial/types";
import { PENDO_BASE_ID, PENDO_CLOSE_TRIAL_GUIDE_SELECTOR, PENDO_CLOSE_UPGRADE_GUIDE_SELECTOR, PENDO_CONCLUDE_UPGRADE_GUIDE_SELECTOR, PENDO_START_TRIAL_CTA_QUERY_SELECTOR, PENDO_UPGRADE_CTA_QUERY_SELECTOR, } from './pendo';
const KEYDOWN_EVENT = 'keydown';
export const useKeyDown = (callback, keyCodes) => {
    useEffect(() => {
        const keyDownHandler = (event) => {
            if (keyCodes.find(currentKeyCode => currentKeyCode === event.code)) {
                callback(event);
            }
        };
        document.addEventListener(KEYDOWN_EVENT, keyDownHandler);
        return () => document.removeEventListener(KEYDOWN_EVENT, keyDownHandler);
    }, [keyCodes, callback]);
};
export const useDateNow = () => {
    const [dateNow, setDateNow] = useState(Date.now());
    useEffect(() => {
        const intervalHandler = setInterval(() => setDateNow(Date.now()), 1000);
        return () => clearInterval(intervalHandler);
    }, []);
    return dateNow;
};
export const useInterval = (callback, delay) => {
    const savedCallback = useRef(null);
    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);
    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }
        if (delay !== null && delay !== undefined) {
            const id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
};
export const useDebounce = ({ value, onChange, initialValue, debounceTime = 0, }) => {
    const [debouncedValue, setDebouncedValue] = useState(initialValue);
    useEffect(() => {
        const timer = setTimeout(() => {
            if (value !== debouncedValue) {
                setDebouncedValue(value);
                onChange(value);
            }
        }, debounceTime);
        return () => clearTimeout(timer);
    }, [value, debouncedValue, onChange, debounceTime]);
};
export const useRefDimensions = ref => {
    var _a, _b;
    const [dimensions, setDimensions] = useState({
        width: 0,
        height: 0,
        clientWidth: 0,
        scrollWidth: 0,
        clientHeight: 0,
        scrollHeight: 0,
        offsetHeight: 0,
        offsetWidth: 0,
    });
    useEffect(() => {
        if (ref.current) {
            const { current } = ref;
            const { clientWidth, scrollWidth, clientHeight, scrollHeight, offsetHeight, offsetWidth } = current;
            const boundingRect = current.getBoundingClientRect();
            const { width, height } = boundingRect;
            setDimensions({
                width,
                height,
                clientWidth,
                scrollWidth,
                clientHeight,
                scrollHeight,
                offsetHeight,
                offsetWidth,
            });
        }
    }, [ref, (_a = ref === null || ref === void 0 ? void 0 : ref.current) === null || _a === void 0 ? void 0 : _a.scrollHeight, (_b = ref === null || ref === void 0 ? void 0 : ref.current) === null || _b === void 0 ? void 0 : _b.scrollWidth]);
    return dimensions;
};
export const usePendoModalMutationObserver = ({ startTrialCtaSelector = PENDO_START_TRIAL_CTA_QUERY_SELECTOR, upgradeCtaSelector = PENDO_UPGRADE_CTA_QUERY_SELECTOR, closeTrialGuideSelector = PENDO_CLOSE_TRIAL_GUIDE_SELECTOR, closeUpgradeGuideSelector = PENDO_CLOSE_UPGRADE_GUIDE_SELECTOR, concludeUpgradeGuideSelector = PENDO_CONCLUDE_UPGRADE_GUIDE_SELECTOR, onCloseTrialGuide, onCloseUpgradeGuide, onConcludeUpgrade, onCtaClick, organizationId, }) => {
    useEffect(() => {
        let $startTrialCta = null;
        let $upgradeCtas = [];
        let $closeTrialGuide = null;
        let $closeUpgradeGuide = null;
        let $concludeUpgradeGuide = null;
        let upgradeCtasWithCampaign;
        let startTrialCampaignId;
        let startTrialCallBack;
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                mutation.addedNodes.forEach(node => {
                    var _a;
                    if (node.nodeType === Node.ELEMENT_NODE && node.id === PENDO_BASE_ID) {
                        $startTrialCta = node.querySelector(startTrialCtaSelector);
                        $upgradeCtas = Array.from(node.querySelectorAll(upgradeCtaSelector));
                        $closeTrialGuide = node.querySelector(closeTrialGuideSelector);
                        $closeUpgradeGuide = node.querySelector(closeUpgradeGuideSelector);
                        if (concludeUpgradeGuideSelector) {
                            $concludeUpgradeGuide = node.querySelector(concludeUpgradeGuideSelector);
                            onConcludeUpgrade &&
                                ($concludeUpgradeGuide === null || $concludeUpgradeGuide === void 0 ? void 0 : $concludeUpgradeGuide.addEventListener('click', onConcludeUpgrade));
                        }
                        startTrialCampaignId = (_a = $startTrialCta === null || $startTrialCta === void 0 ? void 0 : $startTrialCta.getAttribute('aria-label')) === null || _a === void 0 ? void 0 : _a.split(':')[1];
                        upgradeCtasWithCampaign = $upgradeCtas
                            .map(element => {
                            var _a, _b;
                            return ({
                                campaignId: (_a = element.getAttribute('aria-label')) === null || _a === void 0 ? void 0 : _a.split(':')[1],
                                license: (_b = element.getAttribute('aria-label')) === null || _b === void 0 ? void 0 : _b.split(':')[2],
                                element,
                            });
                        })
                            .filter(pendoCta => !!pendoCta.campaignId)
                            .map(({ campaignId, license, element }) => ({
                            callback: () => onCtaClick({ campaignId, license, organizationId, mode: Mode.UPGRADE }),
                            element,
                        }));
                        startTrialCallBack = () => onCtaClick({
                            campaignId: startTrialCampaignId,
                            organizationId,
                            mode: Mode.TRIAL,
                        });
                        $startTrialCta === null || $startTrialCta === void 0 ? void 0 : $startTrialCta.addEventListener('click', startTrialCallBack);
                        upgradeCtasWithCampaign === null || upgradeCtasWithCampaign === void 0 ? void 0 : upgradeCtasWithCampaign.forEach(({ callback, element }) => element.addEventListener('click', callback));
                        onCloseTrialGuide && ($closeTrialGuide === null || $closeTrialGuide === void 0 ? void 0 : $closeTrialGuide.addEventListener('click', onCloseTrialGuide));
                        onCloseUpgradeGuide &&
                            ($closeUpgradeGuide === null || $closeUpgradeGuide === void 0 ? void 0 : $closeUpgradeGuide.addEventListener('click', onCloseUpgradeGuide));
                    }
                });
            });
        });
        observer.observe(document.body, {
            attributes: false,
            childList: true,
            characterData: false,
            subtree: true,
        });
        return () => {
            observer.disconnect();
            upgradeCtasWithCampaign === null || upgradeCtasWithCampaign === void 0 ? void 0 : upgradeCtasWithCampaign.forEach(({ callback, element }) => element.removeEventListener('click', callback));
            $startTrialCta &&
                startTrialCampaignId &&
                $startTrialCta.removeEventListener('click', startTrialCallBack);
            $closeTrialGuide &&
                onCloseTrialGuide &&
                $closeTrialGuide.removeEventListener('click', onCloseTrialGuide);
            $closeUpgradeGuide &&
                onCloseUpgradeGuide &&
                $closeUpgradeGuide.removeEventListener('click', onCloseUpgradeGuide);
            $concludeUpgradeGuide &&
                onConcludeUpgrade &&
                $concludeUpgradeGuide.removeEventListener('click', onConcludeUpgrade);
            upgradeCtasWithCampaign === null || upgradeCtasWithCampaign === void 0 ? void 0 : upgradeCtasWithCampaign.forEach(({ callback, element }) => element.removeEventListener('click', callback));
        };
    }, [onCtaClick, startTrialCtaSelector, upgradeCtaSelector, organizationId]);
};
/**
 * @deprecated Use ```@ucc-messaging/inbox-common-ui``` instead
 * */
export const TYPING_INDICATOR_SET_TO_FALSE_TIME_OUT = 10000;
/**
 * @deprecated Use ```@ucc-messaging/inbox-common-ui``` instead
 * */
export const useTypingIndicatorEvents = ({ onTyping, debouncedValue = 1000, intervalValue = TYPING_INDICATOR_SET_TO_FALSE_TIME_OUT / 2, }) => {
    const typingTimeOut = useRef();
    const typingInterval = useRef();
    const isTyping = useRef(null);
    useEffect(() => {
        if (onTyping) {
            const onBeforeUnload = () => onTyping(false);
            window.addEventListener('beforeunload', onBeforeUnload);
            return () => {
                window.removeEventListener('beforeunload', onBeforeUnload);
            };
        }
    }, [onTyping]);
    if (!onTyping) {
        return;
    }
    const handleOnInput = () => {
        clearTimeout(typingTimeOut.current);
        if (isTyping.current !== true) {
            isTyping.current = true;
            onTyping(true);
            typingInterval.current = setInterval(() => {
                typingInterval.current && onTyping(true);
            }, intervalValue);
        }
        typingTimeOut.current = setTimeout(() => {
            clearInterval(typingInterval.current);
            isTyping.current = false;
            onTyping(false);
        }, debouncedValue);
    };
    return handleOnInput;
};
/**
 * @deprecated Use ```@ucc-messaging/inbox-common-ui``` instead
 * */
export const useReadingIndicator = ({ conversationEvents, onSendReadIndicator, }) => {
    // when chat window is scrolled to bottom readIndicator will be set to true
    const [readIndicator, setReadIndicator] = useState(false);
    const [isDocumentFocused, setIsDocumentFocused] = useState(document.hasFocus());
    // when dont want to send the read event more than once unless the conversation events have changed
    const readAllMessage = useRef(false);
    const lastsMessage = conversationEvents === null || conversationEvents === void 0 ? void 0 : conversationEvents[0];
    const lastMessageIsInbound = isMessageEvent(lastsMessage) && lastsMessage.direction === MessageDirection.INBOUND;
    const sendReadIndicatorEvent = onSendReadIndicator &&
        readIndicator &&
        isDocumentFocused &&
        !readAllMessage.current &&
        lastMessageIsInbound;
    useEffect(() => {
        let hasFocusInterval;
        if (onSendReadIndicator) {
            hasFocusInterval = window.setInterval(() => setIsDocumentFocused(document.hasFocus()), 1000);
        }
        return () => window.clearInterval(hasFocusInterval);
    }, [onSendReadIndicator]);
    // reset everting in a new conversation event comes in
    useEffect(() => {
        if (onSendReadIndicator) {
            readAllMessage.current = false;
            setReadIndicator(false);
        }
    }, [conversationEvents]);
    // when focused and the message content has been scrolled all the way to the bottom send event
    useEffect(() => {
        if (sendReadIndicatorEvent) {
            onSendReadIndicator();
            readAllMessage.current = true;
        }
    }, [sendReadIndicatorEvent]);
    return setReadIndicator;
};
//# sourceMappingURL=hooks.js.map